import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { getIsAdmin, getUserData } from "@icarius-app/selectors";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography } from "@material-ui/core";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import DialogTransition from "@icarius-common/dialogTransition";
import Loader from "@icarius-common/loader";
import ProfileImg from "@icarius-common/profileImg";
import DynamicInput from "@icarius-common/dynamicInputs";
import useHandleForm from "./useHandleForm";
import { getFiles } from "../../selectors";
import ActionFileHandler from "../actionFileHandler";
import TextFieldMultiline from "@icarius-common/textFieldMultiline";

const EditActionDialog = (props) => {
    const { open, isLoading, data, collaboratorList, handleSubmit, handleClose } = props;

    const dispatch = useDispatch();
    const isAdmin = useSelector(getIsAdmin);
    const { code } = useSelector(getUserData);
    const files = useSelector(getFiles);

    const taskCollaborator = collaboratorList.find((item) => item.key === data.collaboratorCode);
    const isResponsible = data.responsibleCode === code;

    const openValidationError = () => {
        dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
    };
    const { formData, setFormValue, submit } = useHandleForm(data, handleSubmit, openValidationError);

    return (
        <Dialog
            open={open}
            TransitionComponent={DialogTransition}
            PaperComponent={PaperDraggable}
            maxWidth={"md"}
            fullWidth
        >
            <div className={"dialog-container"}>
                <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
                    <div style={{ display: "flex", alignItems: "center", gap: 15, marginRight: 20 }}>
                        <ProfileImg image={taskCollaborator.image} size={50} />
                        <div>
                            <Typography className="whiteText" style={{ fontSize: 20 }}>
                                {taskCollaborator.name}
                            </Typography>
                            <Typography className="whiteText" style={{ fontSize: 18, fontWeight: 600 }}>
                                {`${data.name} (${data.statusName} ${getLocalizedString("atTime")} ${data.statusDate})`}
                            </Typography>
                        </div>
                    </div>
                </DialogTitle>
                <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
                <DialogContent>
                    <Loader open={isLoading} />
                    <Grid container spacing={2}>
                        <Grid container item alignItems="center" xs={12} sm={6} md={4}>
                            {!isResponsible && !isAdmin ? (
                                <DynamicInput
                                    fieldConfig={{ type: "date" }}
                                    label={getLocalizedString("startDate")}
                                    value={data.startDate}
                                />
                            ) : (
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                    <KeyboardDatePicker
                                        disabled={!isResponsible && !isAdmin}
                                        required
                                        label={getLocalizedString("startDate")}
                                        value={formData.startDate}
                                        onChange={(e) => setFormValue(e, "startDate")}
                                        fullWidth
                                        cancelLabel={getLocalizedString("cancel")}
                                        okLabel={getLocalizedString("ok")}
                                        invalidDateMessage=""
                                        minDateMessage=""
                                        format="dd/MM/yyyy"
                                        margin="none"
                                    />
                                </MuiPickersUtilsProvider>
                            )}
                        </Grid>
                        <Grid container item alignItems="center" xs={12} sm={6} md={4}>
                            {!isResponsible && !isAdmin ? (
                                <DynamicInput
                                    fieldConfig={{ type: "date" }}
                                    label={getLocalizedString("endDate")}
                                    value={data.endDate}
                                />
                            ) : (
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                    <KeyboardDatePicker
                                        disabled={!isResponsible && !isAdmin}
                                        required
                                        label={getLocalizedString("endDate")}
                                        value={formData.endDate}
                                        onChange={(e) => setFormValue(e, "endDate")}
                                        fullWidth
                                        minDate={formData.startDate}
                                        cancelLabel={getLocalizedString("cancel")}
                                        okLabel={getLocalizedString("ok")}
                                        invalidDateMessage=""
                                        minDateMessage=""
                                        format="dd/MM/yyyy"
                                        margin="none"
                                    />
                                </MuiPickersUtilsProvider>
                            )}
                        </Grid>
                        <Grid container item alignItems="center" xs={12} sm={6} md={4}>
                            {!isResponsible && !isAdmin ? (
                                <DynamicInput
                                    fieldConfig={{ type: "date" }}
                                    label={getLocalizedString("reminderDate")}
                                    value={data.reminderDate}
                                />
                            ) : (
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                                    <KeyboardDatePicker
                                        disabled={!isResponsible && !isAdmin}
                                        label={getLocalizedString("reminderDate")}
                                        value={formData.reminderDate}
                                        onChange={(e) => setFormValue(e, "reminderDate")}
                                        fullWidth
                                        minDate={formData.reminderDate}
                                        cancelLabel={getLocalizedString("cancel")}
                                        okLabel={getLocalizedString("ok")}
                                        invalidDateMessage=""
                                        minDateMessage=""
                                        format="dd/MM/yyyy"
                                        margin="none"
                                    />
                                </MuiPickersUtilsProvider>
                            )}
                        </Grid>
                        <Grid container item alignItems="center" xs={12}>
                            {!isResponsible && !isAdmin ? (
                                <DynamicInput label={getLocalizedString("description")} value={data.description} />
                            ) : (
                                <TextFieldMultiline
                                    disabled={!isResponsible && !isAdmin}
                                    fullWidth
                                    rows={5}
                                    margin={"none"}
                                    label={getLocalizedString("description")}
                                    value={formData.description}
                                    onChange={(e) => setFormValue(e.target.value, "description")}
                                    inputProps={{ maxLength: 100, spellCheck: "false" }}
                                    helperText={getLocalizedString("onboardingTrackingDescriptionHelperText")}
                                />
                            )}
                        </Grid>
                        <ActionFileHandler actionCode={data.code} files={files} />
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonDialogAction onClick={handleClose} text={getLocalizedString("disagree")} />
                    <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
                </DialogActions>
            </div>
        </Dialog>
    );
};

export default EditActionDialog;
